import React, { useEffect, useMemo, useState } from 'react';

import classnames from 'classnames';

import Drawer from 'components/uiLibrary/Drawer';
import { PrimaryButton, TextButton } from 'components/uiLibrary/LinkButton';
import SpriteIcon from 'components/uiLibrary/SpriteIcon';
import Tooltip from 'components/uiLibrary/Tooltip';
import Typography from 'components/uiLibrary/Typography';

import { SUB_COMPONENTS } from 'components/Globals/Analytics/constants';
import EntityName from 'components/Globals/EntityName';
import WorkRoleField from 'components/Globals/FormFields/WorkRoleField';
import OperatorBlock from 'components/Globals/Layout/Header/GlobalSearch/OperatorBlock/OperatorBlock';

import { FILTER_AGGREGATION_SEARCH, PRODUCTIONS } from 'constants/endpointConstants';
import {
  AGGREGATION_TYPES,
  BOOLEAN_SEARCH_PARTS,
  DATE_FORMATS,
  DEFAULT_BOOLEAN_QUERY,
  DEFAULT_OPERATOR,
  EVENTS,
  TP,
  URL_SLUG_TYPES,
} from 'constants/index';
import {
  CASTING_SEARCH_CATEGORIES,
  CASTING_SEARCH_CATEGORIES_KEYS,
  CASTING_SEARCH_FILTER_CATEGORIES,
} from 'constants/search';

import { findCreditGroupByRole } from 'utils/casting';
import { createDate } from 'utils/date';
import useAppContext from 'utils/hooks/useAppContext';
import { useQuery } from 'utils/react-query';

import searchQueries from 'containers/Search/queries';
import workQueries from 'containers/Work/queries';

import { Trans, useTranslation } from 'src/i18n';

import { ENTITY_TYPES } from 'operabase-router/constants';

import AddAnotherRole from '../../AddAnotherRole';
import RoleOverlap from '../../RoleOverlap';
import CastingInput from '../CastingInput';
import SearchDrawer from '../SearchDrawer';

import classes from './MobileSearchSelected.module.scss';

const WorkRole = ({ workId, role, onClick }) => {
  const { obRouteContext } = useAppContext();
  const { filters } = obRouteContext?.linkProps || {};

  const { data: fetchedWork } = useQuery(
    searchQueries.getSelectedOption({
      identifier: workId,
      endpoint: FILTER_AGGREGATION_SEARCH(PRODUCTIONS),
      queryParams: {
        work_id: workId,
        aggregation_type: AGGREGATION_TYPES.WORK,
      },
      queryConfig: {
        enabled: !!workId,
      },
    }),
  );
  const { data: castCrewData } = useQuery(
    workQueries.getCastCrewDetails({
      filters: {
        work_id: workId,
        ...(filters?.[URL_SLUG_TYPES.DATE_FROM] && { [URL_SLUG_TYPES.DATE_FROM]: filters?.[URL_SLUG_TYPES.DATE_FROM] }),
      },
      queryConfig: { enabled: !!workId },
    }),
  );
  const matchedRole = useMemo(() => findCreditGroupByRole(castCrewData, role)?.role, [role, castCrewData]);

  return (
    <div className={classes.root__workRole} onClick={onClick}>
      <Typography size="12" weight="bold" truncate>
        {matchedRole?.name}
      </Typography>
      <Typography truncate>
        <EntityName
          entity={fetchedWork}
          entityType={ENTITY_TYPES.WORK}
          withComposers
          withTranslation={false}
          typographyProps={{
            work: {
              size: 10,
              weight: 'medium',
            },
            composer: {
              size: 10,
              weight: 'regular',
            },
          }}
        />
      </Typography>
    </div>
  );
};

const SearchCategory = ({ category, sinceYear, countries, setSelectedCategory }) => {
  const { t } = useTranslation('NS_CASTING_TOOL');

  const renderSelectedValue = () => {
    if (category.key === CASTING_SEARCH_CATEGORIES_KEYS.SINCE) {
      return (
        <Typography size="12" weight="medium" truncate>
          {sinceYear}
        </Typography>
      );
    }

    if (category.key === CASTING_SEARCH_CATEGORIES_KEYS.COUNTRIES) {
      return (
        <Typography size="12" weight="medium" truncate>
          {countries?.map(country => country.name).join(', ') || t(`${TP}.FN_ALL`)}
        </Typography>
      );
    }

    return '';
  };

  return (
    <div className={classes.root__searchCategory}>
      <Typography size="12" weight="medium">
        {t(category.label)}
      </Typography>
      <div
        className={classes.root__searchCategory_content}
        onClick={() => {
          if ([CASTING_SEARCH_CATEGORIES_KEYS.SINCE, CASTING_SEARCH_CATEGORIES_KEYS.COUNTRIES].includes(category.key)) {
            setSelectedCategory(category);
          }
        }}
      >
        {renderSelectedValue()}
      </div>
    </div>
  );
};

const WorkRoleDrawer = ({
  onClose,
  onChangeHandler,
  booleanQuery,
  setSelectedCategory,
  sinceYear,
  countries,
  index,
  trackingData = {},
}) => {
  const { t } = useTranslation('NS_CASTING_TOOL');
  const { obRouteContext } = useAppContext();
  const { linkProps } = obRouteContext;
  const { filters } = linkProps || {};
  const { value } = booleanQuery || {};
  const searchCategories = useMemo(
    () =>
      CASTING_SEARCH_CATEGORIES.filter(
        category =>
          ![CASTING_SEARCH_CATEGORIES_KEYS.SINCE, CASTING_SEARCH_CATEGORIES_KEYS.COUNTRIES].includes(category.key),
      ),
    [],
  );

  return (
    <Drawer className={classes.drawer} isOpen onClose={onClose}>
      <div className={classes.drawer__header}>
        <SpriteIcon icon="arrow_back" size="16" onClick={onClose} />
        <Typography weight="bold" className={classes.drawer__header_title}>
          {t(`${TP}.ROLE_REPERTOIRE_SEARCH`)}
        </Typography>
        <SpriteIcon icon="close" size="16" onClick={onClose} />
      </div>
      <div className={classes.drawer__content}>
        {searchCategories.map(category => (
          <div className={classes.search__field} key={category.key}>
            <Typography size="12" weight="medium" truncate>
              {t(category.label)}
              {!!category?.helpText && (
                <Tooltip title={t(category.helpText)}>
                  <span>
                    <SpriteIcon icon="help" size={14} className={classes.search__helpIcon} />
                  </span>
                </Tooltip>
              )}
            </Typography>
            {category.key !== CASTING_SEARCH_CATEGORIES_KEYS.ROLES ? (
              <CastingInput
                setSelectedCategory={setSelectedCategory}
                category={category}
                sinceYear={sinceYear}
                countries={countries}
                workId={value?.[URL_SLUG_TYPES.WORK]}
              />
            ) : (
              <WorkRoleField
                value={value?.[URL_SLUG_TYPES.WORK_ROLE] || {}}
                onChange={selectedRole => {
                  onChangeHandler(URL_SLUG_TYPES.WORK_ROLE, selectedRole, index);
                }}
                workId={value?.[URL_SLUG_TYPES.WORK]}
                showWorkComposer={false}
                showLabel={false}
                filters={{
                  ...filters,
                  date_from: sinceYear
                    ? createDate(`${sinceYear}`).format(DATE_FORMATS.FULL_DATE)
                    : createDate()
                        .subtract(5, 'year')
                        .format(DATE_FORMATS.FULL_DATE),
                }}
                trackingData={{
                  ...trackingData,
                  subComponent: SUB_COMPONENTS.WORK_ROLE,
                }}
              />
            )}
          </div>
        ))}
      </div>
      <div className={classes.drawer__actions}>
        <PrimaryButton onClick={onClose}>{t(`${TP}.m_APPLY`)}</PrimaryButton>
      </div>
    </Drawer>
  );
};

const MobileSearchSelected = ({
  booleanQuery = [],
  setBooleanQuery,
  onDeleteHandler,
  sinceYear,
  countries,
  onChangeHandler,
  composer,
  setComposer,
  isDesiredRole,
  isRoleDebut,
  trackingData = {},
}) => {
  const { t } = useTranslation('NS_CASTING_TOOL');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [workRoleDrawerOpen, setWorkRoleDrawerOpen] = useState(false);

  useEffect(() => {
    const handleAddAnotherRole = () => {
      setBooleanQuery(prevQuery => {
        const updatedQuery = [...prevQuery, DEFAULT_OPERATOR, DEFAULT_BOOLEAN_QUERY];
        return updatedQuery;
      });
    };

    window.addEventListener(EVENTS.ADD_ANOTHER_ROLE, handleAddAnotherRole);

    return () => {
      window.removeEventListener(EVENTS.ADD_ANOTHER_ROLE, handleAddAnotherRole);
    };
  }, []);

  return (
    <div className={classes.root}>
      <div className={classes.root__search}>
        <div className={classes.root__roleSearch}>
          {booleanQuery?.map((item, index) => {
            const showOperator = isRoleDebut || isDesiredRole ? index > 2 : index > 0;

            if (item?.type === BOOLEAN_SEARCH_PARTS.OPERATOR) {
              return null;
            }

            return (
              <div
                key={index}
                className={classnames(classes.root__roleSearch_content, {
                  [classes.root__roleSearch_roleDebut]: !showOperator,
                })}
              >
                {(isRoleDebut || isDesiredRole) && (
                  <>
                    {index === 0 && (
                      <Typography size={12} weight="medium" className={classes.root__roleSearch_roleDebutTitle}>
                        <Trans
                          i18nKey={isDesiredRole ? `${TP}.FN_WITH_DESIRED_ROLE` : `${TP}.ROLE_DEBUT_FOR`}
                          ns="NS_CASTING_TOOL"
                          components={{ b: <b /> }}
                        />
                      </Typography>
                    )}
                    {index === 2 && (
                      <Typography size={12} weight="medium" className={classes.root__roleSearch_roleDebutTitle}>
                        {t(`${TP}.SHOULD_HAVE_PERFORMED`)}
                      </Typography>
                    )}
                  </>
                )}
                {showOperator && booleanQuery?.[index - 1]?.type === BOOLEAN_SEARCH_PARTS.OPERATOR && (
                  <div className={classes.operatorBlockContainer}>
                    <OperatorBlock
                      value={booleanQuery?.[index - 1]?.value || DEFAULT_OPERATOR}
                      onSelect={option => {
                        setBooleanQuery(prevQuery => {
                          const tempQuery = [...prevQuery];
                          tempQuery[index - 1] = {
                            ...option,
                            type: BOOLEAN_SEARCH_PARTS.OPERATOR,
                          };
                          return tempQuery;
                        });
                      }}
                      className={classes.operatorBlock}
                      trackingData={trackingData}
                    />
                  </div>
                )}
                {item?.type === BOOLEAN_SEARCH_PARTS.QUERY && (
                  <div className={classes.root__roleSearch_item}>
                    <Typography size="12" weight="medium">
                      {t(`${TP}.FN_ROLE_TITLE`)} / {t(`${TP}.m_MUSICALWORK`)}
                    </Typography>
                    <WorkRole
                      workId={item?.value?.[URL_SLUG_TYPES.WORK]}
                      role={item?.value?.[URL_SLUG_TYPES.WORK_ROLE]}
                      onClick={() => {
                        setWorkRoleDrawerOpen(true);
                        setSelectedIndex(index);
                      }}
                    />
                  </div>
                )}
                {index > 0 && (
                  <TextButton
                    leftIcon={<SpriteIcon icon="delete" size="16" />}
                    styles={{
                      root: classnames(classes.deleteButton, {
                        [classes.deleteButton_roleDebut]: !showOperator,
                      }),
                    }}
                    onClick={() => onDeleteHandler(index)}
                    trackingData={{
                      ...trackingData,
                      subComponent: SUB_COMPONENTS.DELETE_CTA,
                    }}
                  />
                )}
              </div>
            );
          })}
        </div>
        <div className={classes.root__searchCategories}>
          {CASTING_SEARCH_FILTER_CATEGORIES.map(category => (
            <SearchCategory
              key={category.key}
              category={category}
              sinceYear={sinceYear}
              countries={countries}
              setSelectedCategory={setSelectedCategory}
            />
          ))}
        </div>
      </div>
      <div className={classes.root__divider} />
      <div className={classes.root__otherRoles}>
        <AddAnotherRole trackingData={trackingData} />
        <RoleOverlap vertical limit={10} trackingData={trackingData} />
      </div>
      {selectedCategory?.key && (
        <SearchDrawer
          category={selectedCategory}
          onClose={() => setSelectedCategory(null)}
          onClick={(key, value) => {
            onChangeHandler(key, value, selectedIndex);
          }}
          countries={countries}
          sinceYear={sinceYear}
          composer={composer}
          booleanQuery={booleanQuery}
          setComposer={setComposer}
          workId={booleanQuery?.[selectedIndex]?.value?.[URL_SLUG_TYPES.WORK]}
          trackingData={trackingData}
        />
      )}
      {workRoleDrawerOpen && (
        <WorkRoleDrawer
          onClose={() => {
            setWorkRoleDrawerOpen(false);
            setSelectedIndex(null);
          }}
          onChangeHandler={onChangeHandler}
          index={selectedIndex}
          booleanQuery={booleanQuery[selectedIndex]}
          selectedCategory={selectedCategory}
          setSelectedCategory={setSelectedCategory}
          sinceYear={sinceYear}
          countries={countries}
          trackingData={trackingData}
        />
      )}
    </div>
  );
};

export default MobileSearchSelected;
